/**
 * Public help category renderer. /help/:slug -> renders all markdown
 * guides under docs/help/:slug/ as one scrollable page.
 *
 * Replaces the previous "click-through to GitHub" approach. The Manos
 * repository is private, so the GitHub links 404'd for every user --
 * a real production papercut. Rendering in-app fixes that, plus reads
 * better (no leaving the help-centre flow).
 *
 * Source-of-truth for guides: docs/help/{category}/*.md, frontmatter
 * + GFM markdown. Vite imports the whole tree at build time via
 * import.meta.glob, so this route doesn't read filesystem at request
 * time -- safe for serverless runtimes that don't include the docs/
 * tree in the runtime FS layout.
 *
 * Doesn't conflict with the authed /help/messages/* routes (those
 * live under the _app layout at higher specificity than this $slug
 * route; React Router resolves _app.help.messages.* first for that
 * exact path).
 */

import { useLoaderData, Link } from "react-router";
import type { LoaderFunctionArgs } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CaretLeft } from "@phosphor-icons/react";

// Eager glob: every help markdown file gets bundled at build time.
// Path is relative to THIS file (web/routes/), going up two levels to
// reach docs/help/ at the repo root. The `import: 'default'` + `?raw`
// query yields a Record<path, string> of file content keyed by path.
const HELP_FILES = import.meta.glob("../../docs/help/*/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

interface CategoryMeta {
  slug: string;
  title: string;
  description: string;
}

const CATEGORIES: Record<string, CategoryMeta> = {
  "getting-started": {
    slug: "getting-started",
    title: "Getting started",
    description: "Sign up, your trial, and the first 30 minutes.",
  },
  "money-in": {
    slug: "money-in",
    title: "Money in",
    description: "Quotes, invoices, customer payments, BNPL, accountant export.",
  },
  "money-out": {
    slug: "money-out",
    title: "Money out",
    description: "Expenses and recurring costs.",
  },
  "day-to-day": {
    slug: "day-to-day",
    title: "Day to day",
    description: "Inbox, schedule, jobs, and capturing on the go.",
  },
  people: {
    slug: "people",
    title: "People",
    description: "Clients, contacts, sub-contractors, your team.",
  },
  "your-business": {
    slug: "your-business",
    title: "Your business",
    description: "Profile, tax, public directory, accounting and calendar links.",
  },
  subscription: {
    slug: "subscription",
    title: "Subscription",
    description: "Your plan, switching plan, cancelling.",
  },
  troubleshooting: {
    slug: "troubleshooting",
    title: "Troubleshooting",
    description: "Common errors and what to do when something looks wrong.",
  },
};

interface ParsedDoc {
  title: string;
  body: string;
  filename: string;
}

/**
 * Strip the YAML frontmatter (delimited by --- ... ---) and pull the
 * `title` field from it. Falls back to the first H1 if no frontmatter
 * title is present, and finally to the filename if neither exists.
 *
 * Doesn't pull in a YAML library: we control the source files and
 * only need the title field, so a dumb line-scan is enough.
 */
function parseDoc(raw: string, filename: string): ParsedDoc {
  let title = filename.replace(/\.md$/, "").replace(/-/g, " ");
  let body = raw;
  if (raw.startsWith("---")) {
    const end = raw.indexOf("\n---", 3);
    if (end !== -1) {
      const frontmatter = raw.slice(3, end);
      body = raw.slice(end + 4).replace(/^\n+/, "");
      const titleMatch = frontmatter.match(/^\s*title:\s*(.+)$/m);
      if (titleMatch) {
        title = titleMatch[1].trim().replace(/^["']|["']$/g, "");
      }
    }
  }
  // If no frontmatter title, fall back to first H1 in body.
  if (!title || title === filename.replace(/\.md$/, "").replace(/-/g, " ")) {
    const h1 = body.match(/^#\s+(.+)$/m);
    if (h1) {
      title = h1[1].trim();
      // Strip the H1 from the body so we don't render a duplicate
      // heading -- the page-level <h2> covers it.
      body = body.replace(/^#\s+.+\n+/, "");
    }
  } else {
    // Frontmatter title found, still strip the body H1 if present so
    // we don't double-render.
    body = body.replace(/^#\s+.+\n+/, "");
  }
  return { title, body, filename };
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const slug = params.slug;
  if (!slug || !(slug in CATEGORIES)) {
    throw new Response("Help category not found", { status: 404 });
  }
  const category = CATEGORIES[slug];
  const prefix = `../../docs/help/${slug}/`;
  const docs: ParsedDoc[] = Object.entries(HELP_FILES)
    .filter(([path]) => path.startsWith(prefix))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, raw]) => parseDoc(raw, path.slice(prefix.length)));
  return { category, docs };
};

export default function HelpCategory() {
  const { category, docs } = useLoaderData<typeof loader>();
  return (
    <div className="min-h-screen bg-background px-6 py-12">
      <div className="max-w-3xl mx-auto space-y-10">
        <header className="space-y-3">
          <Link
            to="/help"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <CaretLeft className="mr-1 h-4 w-4" weight="bold" />
            Back to help
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">{category.title}</h1>
          <p className="text-base text-muted-foreground">{category.description}</p>
        </header>

        {docs.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No guides in this category yet. Try{" "}
            <Link to="/help/messages/new" className="text-amber-600 hover:underline">
              sending us an inquiry
            </Link>
            .
          </p>
        )}

        <div className="space-y-12">
          {docs.map((doc) => (
            <article
              key={doc.filename}
              id={doc.filename.replace(/\.md$/, "")}
              className="space-y-4 prose prose-stone dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-h2:text-xl prose-h2:font-semibold prose-h2:text-foreground prose-h3:text-base prose-h3:font-semibold prose-h3:text-foreground prose-p:text-sm prose-p:leading-relaxed prose-p:text-muted-foreground prose-li:text-sm prose-li:text-muted-foreground prose-a:text-amber-600 prose-strong:text-foreground"
            >
              <h2 className="!text-2xl !font-bold !tracking-tight !text-foreground">
                {doc.title}
              </h2>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {doc.body}
              </ReactMarkdown>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
